import {
  NODE_SUB_PURPOSE_MAP
} from "./chunk-NUTMEM5W.js";

// src/graph/index.ts
import { z as z3 } from "zod";

// src/graph/node-defs.ts
var GENERATION_OPERATIONS = [
  "gen.image",
  "gen.video",
  "gen.sound",
  "gen.3d",
  "edit.instruct",
  "edit.variation",
  "edit.inpaint",
  "edit.expand",
  "edit.detail",
  "edit.upscale"
];
var DEFS = {
  "trigger.manual": {
    label: "Manual",
    category: "Triggers",
    icon: "play",
    inputs: [],
    outputs: [{ id: "out", type: "any" }],
    params: {}
  },
  "trigger.schedule": {
    label: "Schedule",
    category: "Triggers",
    icon: "clock",
    inputs: [],
    outputs: [{ id: "out", type: "any" }],
    params: { everyMinutes: 60 }
  },
  "trigger.watch": {
    label: "Watch folder",
    category: "Triggers",
    icon: "eye",
    inputs: [],
    outputs: [{ id: "out", type: "image" }],
    params: { path: "inbox" }
  },
  "canvas.in": {
    label: "Canvas In",
    category: "Canvas",
    icon: "frame",
    inputs: [],
    outputs: [{ id: "out", type: "image" }],
    params: { artboardId: "" }
  },
  // The pictures a section holds (Board frames inside its box) or a Compositor holds (its layers); frameIds
  // (comma-separated) narrows them, or stands alone as the frames to work on.
  "canvas.section": {
    label: "Section In",
    category: "Canvas",
    icon: "section",
    inputs: [],
    outputs: [{ id: "out", type: "image" }],
    params: { sectionId: "", frameIds: "" }
  },
  "canvas.note": {
    label: "Note In",
    category: "Canvas",
    icon: "note",
    inputs: [],
    outputs: [{ id: "out", type: "text" }],
    params: { artboardId: "" }
  },
  // Every frame that carries a note (a note anchored to it), the note's words riding on the item as its
  // instruction; the edit nodes take them when their own prompt is empty. A section narrows it.
  "canvas.notes": {
    label: "Notes In",
    category: "Canvas",
    icon: "note",
    inputs: [],
    outputs: [{ id: "out", type: "image" }],
    params: { sectionId: "" }
  },
  "canvas.out": {
    label: "Canvas Out",
    category: "Canvas",
    icon: "frame",
    inputs: [{ id: "in", type: "image" }],
    outputs: [],
    params: { mode: "new", artboardId: "" }
    // 'new' artboards | 'stack' versions on one frame | 'source' back on each item's own frame
  },
  "gen.image": {
    label: "Generate image",
    category: "Generate",
    icon: "sparkles",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "text" }, { id: "ref", type: "image" }, { id: "control", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", width: 512, height: 512, count: 1, provider: "", model: "", engine: "", control: "" }
  },
  "gen.video": {
    label: "Animate",
    category: "Generate",
    icon: "film",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }, { id: "text", type: "text" }],
    outputs: [{ id: "out", type: "video" }],
    params: { prompt: "", seconds: 5, engine: "" }
  },
  "gen.sound": {
    label: "Sound",
    category: "Generate",
    icon: "music",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "text" }],
    outputs: [{ id: "out", type: "audio" }],
    params: { prompt: "", seconds: 10, engine: "" }
  },
  "gen.3d": {
    label: "Image \u2192 3D",
    category: "Generate",
    icon: "box",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "mesh" }],
    params: { engine: "cloud" }
  },
  "edit.instruct": {
    label: "Edit with words",
    category: "Edit",
    icon: "wand",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }, { id: "ref", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", engine: "", keepRest: false, context: false }
  },
  "edit.erase": {
    label: "Erase",
    category: "Edit",
    icon: "eraser",
    app: true,
    inputs: [{ id: "in", type: "image" }, { id: "mask", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { engine: "", keepRest: true }
  },
  // Cut by a selection, no model: keep the selection (the rest transparent) or cut it out, the edge feathered.
  "edit.cutout": {
    label: "Cut out",
    category: "Edit",
    icon: "scissors",
    app: true,
    inputs: [{ id: "in", type: "image" }, { id: "mask", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { mode: "keep", feather: 2 }
  },
  "edit.segment": {
    label: "Select subject",
    category: "Edit",
    icon: "scan",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", points: "", engine: "" }
  },
  "edit.detail": {
    label: "Face detail",
    category: "Edit",
    icon: "smile",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", strength: 0.4, engine: "" }
  },
  "edit.interpolate": {
    label: "Smooth motion",
    category: "Edit",
    icon: "fast-forward",
    app: true,
    inputs: [{ id: "in", type: "video" }],
    outputs: [{ id: "out", type: "video" }],
    params: { multiplier: 2, engine: "" }
  },
  "edit.vectorize": {
    label: "Vectorize",
    category: "Edit",
    icon: "pen-tool",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { colors: 16, detail: "balanced", engine: "" }
  },
  "text.describe": {
    label: "Describe image",
    category: "Text",
    icon: "eye",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "text" }],
    params: { engine: "", model: "" }
    // model: a GET /prompt-tools choice id; empty = the usual order
  },
  "text.enhance": {
    label: "Enhance prompt",
    category: "Text",
    icon: "sparkle",
    app: true,
    inputs: [{ id: "in", type: "text" }],
    outputs: [{ id: "out", type: "text" }],
    params: { style: "", model: "" }
  },
  // A hosted model tool from the engine's hosted-tool table; `tool` is its slug, the rest are that tool's options.
  "tool.run": {
    label: "Model tool",
    category: "Generate",
    icon: "wand-2",
    app: true,
    inputs: [{ id: "in", type: "any" }, { id: "ref", type: "any" }, { id: "audio", type: "any" }, { id: "video", type: "any" }],
    outputs: [{ id: "out", type: "any" }],
    params: { tool: "", model: "", prompt: "", lighting: "", garment: "", pose: "", scribble: false, influence: 0.75, quality: "", scale: "", direction: "", seconds: 5, aspect: "", task: "", language: "", voice: "", polycount: 3e4 }
  },
  "edit.variation": {
    label: "Variation",
    category: "Edit",
    icon: "shuffle",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { creativity: 0.5, prompt: "" }
  },
  "edit.inpaint": {
    label: "Inpaint",
    category: "Edit",
    icon: "paintbrush",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }, { id: "mask", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", denoise: 1, growMask: 8, keepRest: true }
  },
  "edit.move": {
    label: "Move object",
    category: "Edit",
    icon: "move",
    inputs: [{ id: "in", type: "image" }, { id: "mask", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { dx: 0, dy: 0, blend: true }
  },
  "edit.expand": {
    label: "Expand",
    category: "Edit",
    icon: "expand",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { left: 0, right: 0, top: 0, bottom: 0, prompt: "" }
  },
  "edit.upscale": {
    label: "Upscale",
    category: "Edit",
    icon: "arrow-up",
    app: true,
    generation: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { scale: 2, method: "fast", creativity: 0.3, engine: "", tiles: "auto" }
  },
  "edit.rembg": {
    label: "Remove background",
    category: "Edit",
    icon: "eraser",
    app: true,
    inputs: [{ id: "in", type: "image" }, { id: "ref", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { engine: "", beneath: false }
  },
  "edit.bgfree": {
    label: "Repaint without background",
    category: "Edit",
    icon: "sparkles",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { prompt: "", match: true, fallback: "cut" }
  },
  "edit.adjust": {
    label: "Adjust",
    category: "Edit",
    icon: "sliders",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { brightness: 1, contrast: 1, saturation: 1, hue: 0, temperature: 0, blur: 0, sharpen: 0, grayscale: false }
  },
  // Cut-out care: instant, no model. What a lifted subject needs afterwards.
  "edit.alpha": {
    label: "Clean cut-out",
    category: "Edit",
    icon: "eraser",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { specks: 400, faint: true, defringe: true, edge: "none" }
    // specks: area in px, 0 keeps them; edge: none | soften | harden
  },
  "edit.match": {
    label: "Match tone",
    category: "Edit",
    icon: "palette",
    app: true,
    inputs: [{ id: "in", type: "image" }, { id: "ref", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    // strength: how far the mean colour moves toward the reference, 0..1. beneath: the reference is the ring round
    // the frame on the board. when: 'always', or 'off' (only a frame flagged tone or much brighter than the ring).
    params: { strength: 0.5, beneath: false, when: "always" }
  },
  "edit.shadow": {
    label: "Contact shadow",
    category: "Edit",
    icon: "sun-dim",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    // of the cut-out's width; the shadow is its own layer. ground: write it as the frame's ground layer (when it has none) and pass the picture through.
    params: { strength: 0.34, width: 0.36, height: 0.075, ground: false }
  },
  // The night grade: a frame's mid-tones pulled toward the board's light at its foot, highlights kept, a share of the cast, never brightening.
  "edit.grade": {
    label: "Grade to light",
    category: "Edit",
    icon: "sun-dim",
    app: true,
    inputs: [{ id: "in", type: "image" }, { id: "ref", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { strength: 0.75, lift: 14, cast: 0.4 }
    // ref: the picture the light field is read from; without one, the board's background frame
  },
  // An audit over frames: flags written on them (bg, cut, tone, ground, foot, lost), the numbers in the item's meta, the picture passed through.
  "edit.inspect": {
    label: "Inspect",
    category: "Edit",
    icon: "search",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { checks: "bg,cut,tone,ground,foot,lost", write: true }
  },
  // Data Dune: the desktop places a live WebGPU frame for it; the engine DAG cannot execute it.
  "render.pigment": {
    label: "Data Dune",
    category: "Edit",
    icon: "wind",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { look: "drift", turbulence: 1.5, wanderRadius: 0.075, masterSize: 0.92, relief: 0.9, climbHeight: 0.3, seed: 1 }
  },
  "edit.crop": {
    label: "Crop",
    category: "Edit",
    icon: "crop",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { x: 0, y: 0, w: 1, h: 1 }
    // fractions of the source
  },
  "edit.transform": {
    label: "Flip & rotate",
    category: "Edit",
    icon: "rotate",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { flipH: false, flipV: false, rotate: 0 }
  },
  "edit.split-grid": {
    label: "Split image grid",
    category: "Edit",
    icon: "grid",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { columns: 2, rows: 2 }
  },
  // Retired from the palette: stacking images is the Layers editor now. Saved graphs keep running.
  "compose.composite": {
    label: "Composite",
    category: "Compose",
    icon: "layers",
    legacy: true,
    app: true,
    inputs: [{ id: "a", type: "image" }, { id: "b", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { anchor: "center", scalePct: 50, opacity: 1, blend: "over" }
    // a = base, b = overlay
  },
  "compose.text": {
    label: "Text overlay",
    category: "Compose",
    icon: "type",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { text: "", size: 64, color: "#ffffff", anchor: "bottom", bold: true }
    // {{text}} → item.text
  },
  "compose.resize": {
    label: "Resize",
    category: "Compose",
    icon: "maximize",
    app: true,
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { width: 1024, height: 1024, fit: "cover" }
  },
  "compose.adjust": {
    label: "Adjust",
    category: "Compose",
    icon: "sliders",
    inputs: [{ id: "in", type: "image" }],
    outputs: [{ id: "out", type: "image" }],
    params: { brightness: 1, saturation: 1, blur: 0, grayscale: false }
  },
  "text.prompt": {
    label: "Prompt",
    category: "Text",
    icon: "type",
    inputs: [{ id: "in", type: "text" }],
    outputs: [{ id: "out", type: "text" }],
    params: { text: "" }
    // {{text}} is replaced with the incoming item's text
  },
  "logic.if": {
    label: "Filter",
    category: "Logic",
    icon: "filter",
    inputs: [{ id: "in", type: "any" }],
    outputs: [{ id: "yes", type: "any" }, { id: "no", type: "any" }],
    params: { contains: "" }
    // matched against text / title / prompt
  },
  "logic.merge": {
    label: "Merge",
    category: "Logic",
    icon: "merge",
    inputs: [{ id: "a", type: "any" }, { id: "b", type: "any" }],
    outputs: [{ id: "out", type: "any" }],
    params: {}
  },
  "files.read": {
    label: "Read folder",
    category: "Files",
    icon: "folder",
    inputs: [],
    outputs: [{ id: "out", type: "image" }],
    params: { path: "outputs" }
  },
  "files.save": {
    label: "Save to folder",
    category: "Files",
    icon: "save",
    inputs: [{ id: "in", type: "image" }],
    outputs: [],
    params: { path: "exports" }
  }
};
for (const operation of GENERATION_OPERATIONS) DEFS[operation].params.generation = null;
var GRAPH_NODE_DEFS = DEFS;
var APP_OPERATIONS = Object.keys(DEFS).filter((type) => DEFS[type].app);
function graphNodeDef(type) {
  return Object.hasOwn(DEFS, type) ? DEFS[type] : void 0;
}
function customWorkflowDef(workflow) {
  const params = {};
  for (const item of workflow.params ?? []) params[item.key] = item.value;
  return {
    label: workflow.name,
    category: "ComfyUI",
    icon: "workflow",
    custom: workflow.id,
    inputs: [...workflow.inputs?.image ? [{ id: "in", type: "image" }] : [], { id: "text", type: "text" }],
    outputs: [{ id: "out", type: workflow.output ?? "image" }],
    params,
    exposed: workflow.params ?? []
  };
}
var CUSTOM_NODE_PREFIX = "comfy.custom:";
function resolveNodeDef(type, custom = {}) {
  const builtin = graphNodeDef(type);
  if (builtin) return builtin;
  if (!type.startsWith(CUSTOM_NODE_PREFIX)) return null;
  const id = type.slice(CUSTOM_NODE_PREFIX.length);
  return Object.hasOwn(custom, id) ? customWorkflowDef(custom[id]) : null;
}
function graphNodeDefs(custom = {}) {
  const defs = { ...GRAPH_NODE_DEFS };
  for (const id of Object.keys(custom)) defs[`${CUSTOM_NODE_PREFIX}${id}`] = customWorkflowDef(custom[id]);
  return defs;
}

// src/graph/desktop-update.ts
import { z } from "zod";
var DesktopUpdateStateSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("idle"), message: z.string().optional() }),
  z.object({ status: z.literal("checking") }),
  z.object({ status: z.literal("available"), version: z.string().min(1) }),
  z.object({ status: z.literal("downloading"), version: z.string().min(1), progress: z.number().min(0).max(1).optional() }),
  z.object({ status: z.literal("ready"), version: z.string().min(1) }),
  z.object({ status: z.literal("installing"), version: z.string().min(1) }),
  z.object({ status: z.literal("error"), message: z.string(), retry: z.enum(["check", "download", "install"]), version: z.string().optional() })
]);

// src/graph/scene-assets.ts
import { z as z2 } from "zod";
var SceneAssetSchema = z2.object({
  id: z2.string().min(1),
  title: z2.string().min(1),
  author: z2.string().optional(),
  license: z2.object({ name: z2.string(), url: z2.string().optional(), commercial: z2.boolean().optional() }).optional()
});
var sceneFile = z2.string().regex(/^\/outputs\/[\w.-]+$/, "Expected a scene-owned output file");
var SceneEnvironmentSchema = SceneAssetSchema.extend({
  file: sceneFile,
  intensity: z2.number().min(0).max(5).default(1),
  background: z2.boolean().default(true)
});
var SceneMaterialSchema = SceneAssetSchema.extend({
  maps: z2.object({ color: sceneFile.optional(), normal: sceneFile.optional(), roughness: sceneFile.optional(), metalness: sceneFile.optional(), ao: sceneFile.optional() }),
  repeat: z2.number().min(0.1).max(20).default(1)
});
var SceneLibrarySettingsSchema = z2.object({
  environment: SceneEnvironmentSchema.nullable().optional(),
  material: SceneMaterialSchema.nullable().optional()
});
var ApplyLibraryAssetSchema = z2.object({ assetId: z2.string().min(1) });
var ShapeOptionsSchema = z2.object({
  shape: z2.enum(["box", "sphere", "torus", "knot", "cylinder", "cone", "capsule", "icosahedron"]),
  size: z2.number().min(0.1).max(10).default(1),
  detail: z2.number().int().min(8).max(64).default(32),
  twist: z2.number().min(-180).max(180).default(0),
  color: z2.string().regex(/^#[0-9a-f]{6}$/i).default("#b8b8b8"),
  metalness: z2.number().min(0).max(1).default(0.1),
  roughness: z2.number().min(0).max(1).default(0.35)
});

// src/graph/web-operations.ts
var WEB_NODE_TYPES = {
  ImageNode: "image",
  VideoNode: "video",
  ThreeDNode: "3d",
  TextNode: "text-llm"
};
var WEB_CANVAS_NODES = {
  FrameInput: "canvas.in",
  FrameOutput: "canvas.out"
};
var KEY = (nodeType, purpose, subPurpose) => subPurpose ? `${nodeType}/${purpose}/${subPurpose}` : `${nodeType}/${purpose}`;
var MAP = {
  // ── Image ────────────────────────────────────────────────────────────────
  [KEY("image", "create")]: { operation: "gen.image", app: "generate" },
  [KEY("image", "edit")]: { operation: "edit.instruct", app: "image-edit" },
  [KEY("image", "enhance")]: { operation: "edit.upscale", app: "upscale" },
  [KEY("image", "enhance", "upscale")]: { operation: "edit.upscale", app: "upscale" },
  [KEY("image", "enhance", "super-resolution")]: { operation: "edit.upscale", app: "upscale" },
  [KEY("image", "enhance", "sharpen")]: { operation: "edit.adjust", app: "sharpen" },
  [KEY("image", "tools", "inpaint")]: { operation: "edit.inpaint", app: "inpaint" },
  [KEY("image", "tools", "outpaint")]: { operation: "edit.expand", app: "expand" },
  [KEY("image", "tools", "background-removal")]: { operation: "edit.rembg", app: "remove-background" },
  [KEY("image", "tools", "object-removal")]: { operation: "edit.erase", app: "erase" },
  [KEY("image", "tools", "segmentation")]: { operation: "edit.segment", app: "segment" },
  [KEY("image", "tools", "captioning")]: { operation: "text.describe", app: "describe" },
  [KEY("image", "tools", "vectorization")]: { operation: "edit.vectorize", app: "vectorize" },
  [KEY("image", "tools", "face-retouching")]: { operation: "edit.detail", app: "enhance" },
  [KEY("image", "tools", "style-transfer")]: { operation: "edit.instruct", app: "styler" },
  [KEY("image", "tools", "reimagine")]: { operation: "tool.run", app: "reimagine" },
  [KEY("image", "tools", "colorization")]: { operation: "tool.run", app: "colorize" },
  [KEY("image", "tools", "relighting")]: { operation: "tool.run", app: "relight" },
  [KEY("image", "tools", "virtual-try-on")]: { operation: "tool.run", app: "virtual-try-on" },
  [KEY("image", "tools", "product-photography")]: { operation: "tool.run", app: "product-photo" },
  [KEY("image", "tools", "depth-estimation")]: { operation: "tool.run", app: "depth-map" },
  [KEY("image", "tools", "pose-estimation")]: { operation: "tool.run", app: "pose-map" },
  [KEY("image", "tools", "edge-detection")]: { operation: "tool.run", app: "edge-map" },
  // ── Video ────────────────────────────────────────────────────────────────
  [KEY("video", "create")]: { operation: "gen.video", app: "animate" },
  [KEY("video", "create", "text-to-video")]: { operation: "gen.video", app: "animate" },
  [KEY("video", "create", "image-to-video")]: { operation: "gen.video", app: "animate" },
  [KEY("video", "edit")]: { operation: "tool.run", app: "edit-video" },
  [KEY("video", "enhance")]: { operation: "tool.run", app: "upscale-video" },
  [KEY("video", "enhance", "video-upscale")]: { operation: "tool.run", app: "upscale-video" },
  [KEY("video", "tools", "frame-interpolation")]: { operation: "edit.interpolate", app: "smooth-video" },
  [KEY("video", "tools", "lipsync")]: { operation: "tool.run", app: "lipsync" },
  [KEY("video", "tools", "video-extend")]: { operation: "tool.run", app: "extend-video" },
  [KEY("video", "tools", "reframe")]: { operation: "tool.run", app: "reframe-video" },
  [KEY("video", "tools", "motion-transfer")]: { operation: "tool.run", app: "motion-transfer" },
  [KEY("video", "tools", "video-captioning")]: { operation: "tool.run", app: "describe-video" },
  // ── Sound ────────────────────────────────────────────────────────────────
  [KEY("sound", "create")]: { operation: "gen.sound", app: "sound" },
  [KEY("sound", "create", "text-to-music")]: { operation: "gen.sound", app: "sound" },
  [KEY("sound", "create", "text-to-sound")]: { operation: "gen.sound", app: "sound" },
  [KEY("sound", "enhance", "audio-enhance")]: { operation: "tool.run", app: "clean-audio" },
  [KEY("sound", "tools", "transcription")]: { operation: "tool.run", app: "transcribe" },
  [KEY("sound", "tools", "voice-conversion")]: { operation: "tool.run", app: "change-voice" },
  [KEY("sound", "tools", "audio-analysis")]: { operation: "tool.run", app: "analyze-audio" },
  // ── 3D ───────────────────────────────────────────────────────────────────
  [KEY("3d", "create")]: { operation: "gen.3d", app: "image-to-3d" },
  [KEY("3d", "create", "image-to-3d")]: { operation: "gen.3d", app: "image-to-3d" },
  // Text to 3D reaches the same operation without an image; no app offers it yet.
  [KEY("3d", "create", "text-to-3d")]: { operation: "gen.3d" },
  [KEY("3d", "edit", "remesh")]: { operation: "tool.run", app: "remesh" },
  [KEY("3d", "edit", "retexture")]: { operation: "tool.run", app: "retexture" },
  // ── Text ─────────────────────────────────────────────────────────────────
  [KEY("text-llm", "create")]: { operation: "text.enhance", app: "refine" },
  [KEY("text-llm", "edit")]: { operation: "text.enhance", app: "refine" }
};
var WEB_NODES_WITHOUT_OPERATION = {
  "image/tools/controlnet": "A guidance input to another operation, not an operation.",
  "image/tools/mask": "Its own editor; the mask it produces is an input.",
  "image/tools/face-swap": "No operation and no app offers it yet.",
  "image/tools/training": "Starts a training job, which outlives a graph run.",
  "image/enhance/denoise": "No operation offers it on its own yet.",
  "image/enhance/restore": "No operation offers it on its own yet.",
  "image/enhance/hdr": "No operation offers it on its own yet.",
  "video/tools/frame-extraction": "A player action on a clip, not a model run.",
  "video/tools/video-segmentation": "No operation offers it yet.",
  "video/tools/ffmpeg": "A container and codec utility, not a model run.",
  "video/tools/training": "Starts a training job, which outlives a graph run.",
  "sound/create/text-to-speech": "No operation offers a voice yet; generation covers music and sound.",
  "sound/tools/waveform": "A display of a clip, not a model run.",
  "3d/edit/part-editing": "Its own editor."
};
function webNodeOperation(nodeType, purpose, subPurpose) {
  if (Object.hasOwn(WEB_CANVAS_NODES, nodeType)) return { operation: WEB_CANVAS_NODES[nodeType] };
  const type = Object.hasOwn(WEB_NODE_TYPES, nodeType) ? WEB_NODE_TYPES[nodeType] : nodeType;
  if (!purpose) return null;
  if (subPurpose) {
    const exact = KEY(type, purpose, subPurpose);
    if (Object.hasOwn(WEB_NODES_WITHOUT_OPERATION, exact)) return null;
    if (Object.hasOwn(MAP, exact)) return MAP[exact];
  }
  const bare = KEY(type, purpose);
  return Object.hasOwn(MAP, bare) ? MAP[bare] : null;
}
var WEB_NODE_OPERATIONS = MAP;
function webOperationIssues() {
  return Object.entries(MAP).filter(([, value]) => !Object.hasOwn(GRAPH_NODE_DEFS, value.operation)).map(([key, value]) => `${key} names an unavailable operation: ${value.operation}.`);
}

// src/graph/execution-inventory.ts
var NO_EXECUTABLE_ADAPTER = "NO_EXECUTABLE_ADAPTER";
var LOCAL_CANVAS_FAMILIES = [
  "FrameInput",
  "FrameOutput",
  "TextFile",
  "ImageFile",
  "VideoFile",
  "AudioFile",
  "CommentNode",
  "MaskNode",
  "DataTable",
  "VariationGenerator",
  "PromptComposer",
  "BatchGenerator",
  "GalleryGroup",
  "GalleryContainer",
  "LoraModel",
  "ShaderNode",
  "shader"
];
var LOCAL_FAMILY = new Set(LOCAL_CANVAS_FAMILIES);
var PURPOSES = ["create", "edit", "enhance", "tools"];
var UNAVAILABLE_EXPLANATION = {
  sound: "Web sound has no verified request schema, queue type, adapter, persisted result, and canvas output, so it does not enter the generic job queue.",
  "3d": "Web 3D has no verified request schema, queue type, adapter, persisted result, and canvas renderer, so it does not enter the generic job queue."
};
var LOCAL_EXPLANATION = "This is an input, editor, or workflow on the canvas, not a standalone generation. It does not enter the generic job queue.";
function isStructuralNonOperation(explanation) {
  const text = explanation.toLowerCase();
  return text.includes("not an operation") || text.includes("its own editor") || text.includes("outlives a graph run") || text.includes("not a model run") || text.includes("display of a clip");
}
function normalizeFamily(nodeType) {
  if (!nodeType) return void 0;
  const trimmed = nodeType.trim();
  if (!trimmed) return void 0;
  if (Object.hasOwn(WEB_NODE_TYPES, trimmed)) return WEB_NODE_TYPES[trimmed];
  if (trimmed === "text") return "text-llm";
  return trimmed;
}
function routeFor(nodeType, purpose, subPurpose) {
  if (nodeType === "sound" || nodeType === "3d") {
    return { route: "unavailable", explanation: UNAVAILABLE_EXPLANATION[nodeType] };
  }
  if (nodeType === "text-llm") {
    return {
      route: "direct",
      explanation: "Text streams through the AI API. It is not a generic job-queue operation."
    };
  }
  if (subPurpose) {
    const key = `${nodeType}/${purpose}/${subPurpose}`;
    const excused = WEB_NODES_WITHOUT_OPERATION[key];
    if (excused && isStructuralNonOperation(excused)) {
      return { route: "local", explanation: excused };
    }
    if (excused) {
      return {
        route: "queued",
        explanation: `${excused} A selected hosted model may still use the generic queue; that is not a verified typed adapter.`
      };
    }
  }
  return {
    route: "queued",
    explanation: "Generic image or video queue. Reachability is not a claim that every visible setting hits a typed adapter."
  };
}
function executionInventory() {
  const entries = [];
  for (const [nodeType, purposes] of Object.entries(NODE_SUB_PURPOSE_MAP)) {
    for (const purpose of PURPOSES) {
      const subs = purposes[purpose] ?? [];
      if (subs.length === 0) {
        const classified = routeFor(nodeType, purpose);
        entries.push({
          key: `${nodeType}/${purpose}`,
          nodeType,
          purpose,
          route: classified.route,
          explanation: classified.explanation
        });
      }
      for (const subPurpose of subs) {
        const classified = routeFor(nodeType, purpose, subPurpose);
        entries.push({
          key: `${nodeType}/${purpose}/${subPurpose}`,
          nodeType,
          purpose,
          subPurpose,
          route: classified.route,
          explanation: classified.explanation
        });
      }
    }
  }
  return entries;
}
function refuse(explanation) {
  return { admit: false, code: NO_EXECUTABLE_ADAPTER, explanation };
}
function decideGenericJobAdmission(input) {
  const family = normalizeFamily(input.sourceFamily ?? input.nodeType);
  const purpose = input.purpose?.trim() || void 0;
  const subPurpose = input.subPurpose?.trim() || void 0;
  const modelId = input.modelId?.trim() || void 0;
  if (family && LOCAL_FAMILY.has(family)) return refuse(LOCAL_EXPLANATION);
  if (family === "sound" || family === "3d") return refuse(UNAVAILABLE_EXPLANATION[family]);
  if (family === "text-llm") {
    if (input.jobType === "TEXT_TO_IMAGE" || input.jobType === "IMAGE_TO_IMAGE" || input.jobType === "IMAGE_UPSCALE" || input.jobType === "VIDEO_GENERATION") {
      return refuse("Text streams through the AI API. It does not enter the generic image or video queue.");
    }
    return { admit: true, route: "direct" };
  }
  if (family === "image" || family === "video") {
    if (purpose && subPurpose) {
      const key = `${family}/${purpose}/${subPurpose}`;
      const excused = WEB_NODES_WITHOUT_OPERATION[key];
      if (excused && isStructuralNonOperation(excused)) return refuse(excused);
      if (excused && !modelId) return refuse(excused);
    }
    return { admit: true, route: "queued" };
  }
  if (!family) return { admit: true, route: "hosted" };
  return refuse(`No executable adapter is registered for ${family}.`);
}

// src/graph/index.ts
var graphId = z3.string().min(1).max(160);
var coordinate = z3.number().finite();
var hexColor = z3.string().regex(/^#[0-9a-f]{6}$/i);
var StudioProviderConnectionSchema = z3.object({
  status: z3.enum(["missing", "ready", "invalid", "unavailable"]),
  message: z3.string()
});
var StudioParametersSchema = z3.record(z3.string(), z3.unknown()).superRefine((value, ctx) => {
  const seen = /* @__PURE__ */ new Set();
  const visit = (item, depth) => {
    if (item === null || typeof item === "string" || typeof item === "boolean") return true;
    if (typeof item === "number") return Number.isFinite(item);
    if (typeof item !== "object" || depth > 64 || seen.has(item)) return false;
    if (!Array.isArray(item) && Object.getPrototypeOf(item) !== Object.prototype && Object.getPrototypeOf(item) !== null) return false;
    seen.add(item);
    const valid = Object.values(item).every((child) => visit(child, depth + 1));
    seen.delete(item);
    return valid;
  };
  if (!visit(value, 0)) ctx.addIssue({ code: "custom", message: "Graph settings must contain finite JSON values." });
});
var parameters = StudioParametersSchema;
var GraphPortSchema = z3.object({
  id: graphId,
  type: z3.enum(["image", "text", "any", "video", "audio", "mesh"])
}).loose();
var GraphExposedParameterSchema = z3.object({
  key: graphId,
  nodeId: graphId,
  field: graphId,
  type: z3.enum(["text", "number", "model"]),
  value: z3.unknown(),
  label: z3.string().max(500)
}).loose();
var GraphNodeDefSchema = z3.object({
  label: z3.string().min(1).max(500),
  category: z3.string().min(1).max(160),
  icon: z3.string().max(160),
  inputs: z3.array(GraphPortSchema).max(100),
  outputs: z3.array(GraphPortSchema).max(100),
  params: parameters,
  custom: z3.string().max(160).optional(),
  exposed: z3.array(GraphExposedParameterSchema).max(200).optional(),
  /** Kept so saved graphs still run, but no longer offered in the node palette. */
  legacy: z3.boolean().optional()
}).loose();
var GraphDocNodeSchema = z3.object({
  id: graphId,
  type: z3.string().min(1).max(200),
  x: coordinate,
  y: coordinate,
  params: parameters
}).loose();
var GraphDocEdgeSchema = z3.object({
  id: graphId,
  source: graphId,
  sourceHandle: z3.string().max(160).optional(),
  target: graphId,
  targetHandle: z3.string().max(160).optional()
}).loose();
var GraphDocSchema = z3.object({
  nodes: z3.array(GraphDocNodeSchema).max(1e4),
  edges: z3.array(GraphDocEdgeSchema).max(2e4)
}).loose().superRefine((doc, ctx) => {
  if (!parameters.safeParse(doc).success) ctx.addIssue({ code: "custom", message: "Graph documents must contain finite JSON values, including extension fields." });
  const nodeIds = /* @__PURE__ */ new Set();
  for (const [index, node] of doc.nodes.entries()) {
    if (nodeIds.has(node.id)) ctx.addIssue({ code: "custom", path: ["nodes", index, "id"], message: "Node IDs must be unique." });
    nodeIds.add(node.id);
  }
  const edgeIds = /* @__PURE__ */ new Set();
  for (const [index, edge] of doc.edges.entries()) {
    if (edgeIds.has(edge.id)) ctx.addIssue({ code: "custom", path: ["edges", index, "id"], message: "Edge IDs must be unique." });
    edgeIds.add(edge.id);
    for (const end of ["source", "target"]) {
      if (!nodeIds.has(edge[end])) ctx.addIssue({ code: "custom", path: ["edges", index, end], message: "An edge must refer to a node in this graph." });
    }
  }
});
function graphExecutionIssues(doc, definitions = GRAPH_NODE_DEFS) {
  const issues = [];
  const parsed = GraphDocSchema.safeParse(doc);
  if (!parsed.success) return parsed.error.issues.map((issue) => issue.message);
  const definition = (type) => Object.hasOwn(definitions, type) ? definitions[type] : void 0;
  const nodes = new Map(doc.nodes.map((node) => [node.id, node]));
  const indegree = new Map(doc.nodes.map((node) => [node.id, 0]));
  const outgoing = /* @__PURE__ */ new Map();
  for (const node of doc.nodes) {
    if (!definition(node.type)) issues.push(`Tool unavailable: ${node.type} (${node.id}).`);
  }
  for (const edge of doc.edges) {
    const source = nodes.get(edge.source);
    const target = nodes.get(edge.target);
    if (!source || !target) {
      issues.push(`Connection ${edge.id} refers to a missing node.`);
      continue;
    }
    indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
    const targets = outgoing.get(edge.source) ?? [];
    targets.push(edge.target);
    outgoing.set(edge.source, targets);
    const from = definition(source.type);
    const to = definition(target.type);
    if (!from || !to) continue;
    const output = from.outputs.find((port) => port.id === (edge.sourceHandle ?? "out"));
    const input = to.inputs.find((port) => port.id === (edge.targetHandle ?? "in"));
    if (!output || !input) issues.push(`Connection ${edge.id} uses an unavailable port.`);
    else if (output.type !== "any" && input.type !== "any" && output.type !== input.type) issues.push(`Connection ${edge.id} has incompatible port types.`);
  }
  const queue = doc.nodes.filter((node) => indegree.get(node.id) === 0).map((node) => node.id);
  for (let index = 0; index < queue.length; index++) {
    for (const target of outgoing.get(queue[index]) ?? []) {
      const remaining = (indegree.get(target) ?? 0) - 1;
      indegree.set(target, remaining);
      if (remaining === 0) queue.push(target);
    }
  }
  if (queue.length !== doc.nodes.length) issues.push("The graph contains a cycle.");
  return issues;
}
var StudioAssetSchema = z3.object({
  id: graphId,
  name: z3.string().min(1).max(500),
  mediaType: z3.enum(["image", "video", "audio", "mesh", "file"]),
  mimeType: z3.string().min(1).max(160),
  size: z3.number().int().nonnegative(),
  sha256: z3.string().regex(/^[a-f0-9]{64}$/)
}).loose();
var StudioProvenanceSchema = z3.object({
  graphId,
  nodeId: graphId,
  jobId: graphId.optional(),
  inputVersions: z3.array(z3.object({ frameId: graphId, versionId: graphId }).strict()).optional(),
  model: z3.string().max(500).optional(),
  settings: parameters.optional()
}).strict();
var StudioFrameVersionSchema = z3.object({
  id: graphId,
  label: z3.string().max(500),
  assetId: graphId.optional(),
  jobId: graphId.nullable().optional(),
  parentVersionId: graphId.nullable().optional(),
  params: parameters,
  provenance: StudioProvenanceSchema.optional()
}).loose();
var StudioFrameSchema = z3.object({
  id: graphId,
  title: z3.string().max(500),
  kind: z3.enum(["media", "board", "note", "section", "web", "design", "tool", "composition"]),
  x: coordinate,
  y: coordinate,
  w: z3.number().finite().positive(),
  h: z3.number().finite().positive(),
  activeVersion: z3.number().int().nonnegative(),
  params: parameters,
  versions: z3.array(StudioFrameVersionSchema).max(1e4)
}).loose().superRefine((frame, ctx) => {
  if (frame.activeVersion >= Math.max(frame.versions.length, 1)) {
    ctx.addIssue({ code: "custom", path: ["activeVersion"], message: "The active version must exist on this frame." });
  }
});
var StudioGraphSchema = z3.object({
  id: graphId,
  name: z3.string().min(1).max(500),
  doc: GraphDocSchema,
  source: z3.enum(["desktop", "web"]).optional()
}).loose();
var StudioProjectSchema = z3.object({
  schemaVersion: z3.literal(1),
  id: graphId,
  title: z3.string().min(1).max(500),
  artboards: z3.array(StudioFrameSchema).max(1e4),
  graphs: z3.array(StudioGraphSchema).max(1e3),
  assets: z3.array(StudioAssetSchema).max(2e4),
  primaryGraphId: graphId.optional()
}).loose().superRefine((project, ctx) => {
  if (!parameters.safeParse(project).success) ctx.addIssue({ code: "custom", message: "Projects must contain finite JSON values, including extension fields." });
  for (const key of ["artboards", "graphs", "assets"]) {
    const ids = /* @__PURE__ */ new Set();
    project[key].forEach((item, index) => {
      if (ids.has(item.id)) ctx.addIssue({ code: "custom", path: [key, index, "id"], message: `${key} IDs must be unique.` });
      ids.add(item.id);
    });
  }
  const graphs = new Map(project.graphs.map((graph) => [graph.id, graph]));
  const assets = new Set(project.assets.map((asset) => asset.id));
  const versions = /* @__PURE__ */ new Set();
  const frameVersions = new Map(project.artboards.map((frame) => [frame.id, new Set(frame.versions.map((version) => version.id))]));
  if (project.primaryGraphId && !graphs.has(project.primaryGraphId)) {
    ctx.addIssue({ code: "custom", path: ["primaryGraphId"], message: "The primary graph must belong to this project." });
  }
  project.artboards.forEach((frame, frameIndex) => frame.versions.forEach((version, versionIndex) => {
    const path = ["artboards", frameIndex, "versions", versionIndex];
    if (versions.has(version.id)) ctx.addIssue({ code: "custom", path: [...path, "id"], message: "Version IDs must be unique across the project." });
    versions.add(version.id);
    if (version.assetId && !assets.has(version.assetId)) ctx.addIssue({ code: "custom", path: [...path, "assetId"], message: "The version asset must be included in the project." });
    if (version.provenance) {
      const graph = graphs.get(version.provenance.graphId);
      if (!graph?.doc.nodes.some((node) => node.id === version.provenance?.nodeId)) ctx.addIssue({ code: "custom", path: [...path, "provenance"], message: "The producing node must belong to a project graph." });
    }
  }));
  const parents = new Map(project.artboards.flatMap((frame) => frame.versions.map((version) => [version.id, version.parentVersionId])));
  const checked = /* @__PURE__ */ new Set();
  for (const frame of project.artboards) {
    for (const version of frame.versions) {
      if (version.parentVersionId && !versions.has(version.parentVersionId)) {
        ctx.addIssue({ code: "custom", message: `Version ${version.id} refers to a missing parent version.` });
      }
      for (const input of version.provenance?.inputVersions ?? []) {
        if (!frameVersions.get(input.frameId)?.has(input.versionId)) {
          ctx.addIssue({ code: "custom", message: `Version ${version.id} refers to a missing input version.` });
        }
      }
      const chain = /* @__PURE__ */ new Set();
      let cursor = version.id;
      while (cursor && parents.has(cursor) && !checked.has(cursor)) {
        if (chain.has(cursor)) {
          ctx.addIssue({ code: "custom", message: "Frame version history contains a cycle." });
          break;
        }
        chain.add(cursor);
        cursor = parents.get(cursor);
      }
      for (const id of chain) checked.add(id);
    }
  }
});
var StudioWebNodeSchema = z3.object({
  id: graphId,
  type: z3.string().min(1).max(200),
  position: z3.object({ x: coordinate, y: coordinate }).loose(),
  data: parameters
}).loose();
var StudioWebEdgeSchema = z3.object({
  id: graphId,
  source: graphId,
  target: graphId,
  sourceHandle: z3.string().max(160).nullable().optional(),
  targetHandle: z3.string().max(160).nullable().optional()
}).loose();
var StudioWebPageSchema = z3.object({
  nodes: z3.array(StudioWebNodeSchema).max(1e4),
  edges: z3.array(StudioWebEdgeSchema).max(2e4)
}).loose();
var StudioDesktopVersionSchema = z3.object({
  id: graphId,
  label: z3.string().max(500),
  params: parameters,
  imagePath: z3.string().nullable().optional(),
  imageUrl: z3.string().nullable().optional(),
  jobId: graphId.nullable().optional(),
  parentVersionId: graphId.nullable().optional()
}).loose();
var StudioDesktopFrameSchema = z3.object({
  id: graphId,
  title: z3.string().max(500),
  kind: StudioFrameSchema.shape.kind,
  x: coordinate,
  y: coordinate,
  w: z3.number().finite().positive(),
  h: z3.number().finite().positive(),
  activeVersion: z3.number().int().nonnegative(),
  params: parameters,
  versions: z3.array(StudioDesktopVersionSchema)
}).loose();
var StudioDesktopSessionSchema = z3.object({
  id: graphId,
  title: z3.string().min(1).max(500),
  artboards: z3.array(StudioDesktopFrameSchema).max(1e4),
  graphs: z3.array(z3.object({ id: graphId, name: z3.string().min(1).max(500), doc: GraphDocSchema }).loose()).max(1e3)
}).loose();
var StudioFrameInputSchema = z3.object({ frameId: graphId, versionId: graphId }).strict();
var StudioFrameOutputSchema = z3.object({
  frameId: graphId,
  expectedVersionId: graphId.nullable(),
  version: StudioFrameVersionSchema
}).strict();
var StudioExecutionLocationSchema = z3.enum(["desktop", "cloud"]);
var StudioToolRequestSchema = z3.object({
  toolId: graphId,
  location: StudioExecutionLocationSchema,
  inputs: z3.array(StudioFrameInputSchema),
  params: parameters
}).strict();
var StudioToolResultSchema = z3.object({
  jobId: graphId,
  assets: z3.array(StudioAssetSchema)
}).strict();
var StudioSnapshotSchema = z3.object({
  revision: z3.number().int().nonnegative(),
  project: StudioProjectSchema
}).strict();
var StudioSaveSchema = z3.object({
  expectedRevision: z3.number().int().nonnegative(),
  project: StudioProjectSchema
}).strict();
var StudioAssetUploadSchema = z3.object({
  asset: StudioAssetSchema,
  base64: z3.string().max(32 * 1024 * 1024)
}).strict();
var frameSize = z3.number().finite().positive();
var FrameBoxSchema = z3.tuple([coordinate, coordinate, frameSize, frameSize]);
var FramePointSchema = z3.tuple([coordinate, coordinate]);
var FrameImageLevelSchema = z3.object({
  width: z3.number().int().positive(),
  image: z3.string().min(1).max(2e3)
}).strict();
var FieldClassRuleSchema = z3.object({
  /** The zone's colour over the picture, `#rrggbb`. */
  color: z3.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  /** Who may put a foot on it: everyone, no one, or only frames carrying `flag` (a boat on water). */
  feet: z3.enum(["all", "none", "flagged"]).optional(),
  flag: z3.string().regex(/^[\w-]{1,40}$/).optional(),
  /** Frames keep their paint off it (a tree, a building): a frame covering it breaks the rule. */
  keepClear: z3.boolean().optional(),
  note: z3.string().max(300).optional()
}).strict();
var FrameFieldSchema = z3.object({
  image: z3.string().min(1).max(2e3),
  width: z3.number().int().positive(),
  height: z3.number().int().positive(),
  /** Class maps: what each pixel value means. Code 0 is the base zone the eraser gives back to. */
  legend: z3.record(z3.string(), z3.string().max(200)).optional(),
  /** Class maps: each zone's rule, by code. */
  rules: z3.record(z3.string(), FieldClassRuleSchema).optional()
}).strict();
var FrameGroundSchema = z3.object({
  image: z3.string().min(1).max(2e3),
  small: z3.string().max(2e3).optional(),
  box: FrameBoxSchema,
  strength: z3.number().min(0).max(1).optional()
}).strict();
var FrameDeepZoomSchema = z3.object({
  id: z3.string().regex(/^[a-f0-9]{8}$/),
  /** The index page, an outputs path (`deepzoom/<id>/index.html`). */
  index: z3.string().max(200),
  at: z3.string().max(80),
  width: z3.number().int().positive(),
  height: z3.number().int().positive(),
  items: z3.number().int().min(0),
  stills: z3.boolean().optional()
}).strict();
var FrameLockSchema = z3.object({
  by: z3.enum(["hand", "import", "agent", "user"]).optional(),
  at: z3.string().max(80).optional(),
  note: z3.string().max(500).optional()
}).strict();
var FrameSidecarParamsSchema = z3.object({
  locked: z3.boolean().optional(),
  lock: FrameLockSchema.optional(),
  /** Drawn beneath every other frame, never selected or dragged (a plate, a backdrop). */
  background: z3.boolean().optional(),
  /** Draw order offset; the order is `layer + depth at the foot`. */
  layer: z3.number().finite().optional(),
  /** Where the frame stands, relative to its box. */
  foot: FramePointSchema.optional(),
  /** 0 far to 1 near, the last sampled depth at the foot (the canvas re-samples when it has the field). */
  depth: z3.number().min(0).max(1).optional(),
  /** A small copy (about 256 px) drawn while the frame is small on screen; also its hit map. */
  small: z3.string().max(2e3).optional(),
  /** The last mask painted on the frame (white on black, an outputs file), kept for the next inpaint. */
  mask: z3.string().max(2e3).optional(),
  pyramid: z3.array(FrameImageLevelSchema).max(16).optional(),
  ground: FrameGroundSchema.optional(),
  fields: z3.object({ depth: FrameFieldSchema.optional(), classes: FrameFieldSchema.optional() }).strict().optional(),
  /** Audit flags, free strings (`bg`, `cut`, `tone`, …). */
  flags: z3.array(z3.string().max(80)).max(64).optional(),
  deepZoom: FrameDeepZoomSchema.optional(),
  /** Project data that is not Genie's business (a number, a topic, a panel name). */
  meta: z3.record(z3.string(), z3.union([z3.string().max(4e3), z3.number(), z3.boolean(), z3.null()])).optional(),
  /** Where an imported frame came from. */
  origin: z3.object({ source: z3.string().max(2e3), key: z3.string().max(160).optional(), importedAt: z3.string().max(80).optional() }).strict().optional()
});
var ZONE_STANDABLE = /ground|water|floor|road|sand|grass|path|earth|field|snow|street|beach|soil|dirt|lawn|meadow|pond/i;
var ZONE_SKY = /\bsky\b|cloud|heaven/i;
var ZONE_TREES = /tree|bush|hedge|shrub|plant|foliage|wood|forest/i;
var ZONE_COLORS = [
  [ZONE_SKY, "#966eff"],
  [/water|lake|river|sea/i, "#288cff"],
  [ZONE_TREES, "#ff3c3c"],
  [/building|tower|wall|house|rock|solid|stone/i, "#ff961e"],
  [/pond/i, "#00e6d2"],
  [ZONE_STANDABLE, "#ebc85a"]
];
var ZONE_SPARE = ["#e93d82", "#8e4ec6", "#12a594", "#f7d544", "#46a758", "#4c9ffb"];
function zoneRule(field, code) {
  const name = field?.legend?.[String(code)] ?? (code === 0 ? "ground" : `zone ${code}`);
  const set = field?.rules?.[String(code)] ?? {};
  const sky = ZONE_SKY.test(name), ground = !sky && ZONE_STANDABLE.test(name);
  const color = set.color ?? ZONE_COLORS.find(([pattern]) => pattern.test(name))?.[1] ?? ZONE_SPARE[code % ZONE_SPARE.length];
  const feet = set.feet ?? (ground ? "all" : "none");
  return {
    code,
    name,
    color,
    feet,
    ...feet === "flagged" && set.flag ? { flag: set.flag } : {},
    keepClear: set.keepClear ?? (!sky && !ground),
    ...set.note ? { note: set.note } : {}
  };
}
function zoneRules(field) {
  const codes = /* @__PURE__ */ new Set([0]);
  for (const key of [...Object.keys(field?.legend ?? {}), ...Object.keys(field?.rules ?? {})]) {
    const code = Number(key);
    if (Number.isInteger(code) && code >= 0 && code < 256) codes.add(code);
  }
  return [...codes].sort((a, b) => a - b).map((code) => zoneRule(field, code));
}
function zoneTakesFeet(rule, flags = []) {
  return rule.feet === "all" || rule.feet === "flagged" && Boolean(rule.flag) && flags.includes(rule.flag);
}
function frameOrder(params, depthAtFoot) {
  const depth = typeof depthAtFoot === "number" && Number.isFinite(depthAtFoot) ? depthAtFoot : params.depth ?? 0;
  return (params.layer ?? 0) + depth;
}
var CanvasImportVersionSchema = z3.object({
  label: z3.string().max(200),
  image: z3.string().min(1).max(2e3),
  at: z3.string().max(80).optional(),
  /** This version's own sidecars (its ground, its box) and any project data. */
  params: parameters.optional()
}).strict();
var CanvasImportFrameSchema = z3.object({
  /** Stable within the folder; becomes the frame's file folder in the outputs. */
  key: z3.string().regex(/^[\w-]{1,120}$/),
  title: z3.string().max(500),
  kind: z3.enum(["media", "note", "section"]).optional(),
  x: coordinate,
  y: coordinate,
  w: frameSize,
  h: frameSize,
  /** Pixel size of the picture when it differs from the box (a plate that exists only as tiles). */
  width: z3.number().int().positive().optional(),
  height: z3.number().int().positive().optional(),
  params: FrameSidecarParamsSchema.optional(),
  versions: z3.array(CanvasImportVersionSchema).max(1e4),
  /** Index into `versions`; the last one when absent. */
  activeVersion: z3.number().int().nonnegative().optional()
}).strict().superRefine((frame, ctx) => {
  if (frame.activeVersion !== void 0 && frame.activeVersion >= Math.max(frame.versions.length, 1)) {
    ctx.addIssue({ code: "custom", path: ["activeVersion"], message: "The active version must exist on this frame." });
  }
});
var CanvasImportSchema = z3.object({
  version: z3.literal(1),
  title: z3.string().min(1).max(500),
  note: z3.string().max(4e3).optional(),
  /** The document's extent: the Board's first fit, or the scene size when the frames go inside a Compositor. */
  size: z3.object({ width: frameSize, height: frameSize }).strict().optional(),
  /**
   * `board` (default): every frame lands on the Board. `composition`: the Board gets one Compositor
   * frame named after the document and every frame goes inside it as a layer, stacked back to front
   * by its draw order (background first, then layer + depth), so the wall is one thing on the Board.
   */
  container: z3.enum(["board", "composition"]).optional(),
  /** The scene's colour behind everything (composition container only). */
  background: hexColor.optional(),
  frames: z3.array(CanvasImportFrameSchema).max(1e4)
}).strict().superRefine((doc, ctx) => {
  const seen = /* @__PURE__ */ new Set();
  doc.frames.forEach((frame, index) => {
    if (seen.has(frame.key)) ctx.addIssue({ code: "custom", path: ["frames", index, "key"], message: `Frame key "${frame.key}" is used twice.` });
    seen.add(frame.key);
  });
  if (doc.container === "composition") {
    if (doc.frames.length > COMPOSITION_MAX_LAYERS) ctx.addIssue({ code: "custom", path: ["frames"], message: `A Compositor holds at most ${COMPOSITION_MAX_LAYERS} layers.` });
    doc.frames.forEach((frame, index) => {
      if (frame.kind && frame.kind !== "media") ctx.addIssue({ code: "custom", path: ["frames", index, "kind"], message: "Only media frames can go inside a Compositor." });
    });
  }
});
var ArtCanvasFrameSchema = z3.object({
  id: graphId,
  title: z3.string(),
  kind: StudioFrameSchema.shape.kind,
  /** The Compositor frame this one lives in. A frame inside a Compositor is one of its layers, not a Board frame; its x, y, w, h are scene pixels. */
  parentId: graphId.optional(),
  x: coordinate,
  y: coordinate,
  w: z3.number().positive(),
  h: z3.number().positive(),
  activeVersion: z3.number().int().nonnegative(),
  versions: z3.array(z3.object({
    id: graphId,
    label: z3.string(),
    gradient: z3.string(),
    image: z3.string().optional(),
    mediaType: StudioAssetSchema.shape.mediaType.optional(),
    error: z3.string().optional()
  })),
  params: z3.object({
    width: z3.number().optional(),
    height: z3.number().optional(),
    text: z3.string().optional(),
    url: z3.string().optional(),
    favorite: z3.boolean().optional(),
    values: z3.record(z3.string(), z3.union([z3.string(), z3.number(), z3.boolean()])).optional()
  }).extend(FrameSidecarParamsSchema.shape),
  job: z3.object({
    id: graphId,
    status: z3.string(),
    progress: z3.number(),
    error: z3.string().optional(),
    preview: z3.string().optional()
  }).optional()
});
var ArtCanvasLineageSchema = z3.object({
  canvasId: graphId,
  nodes: z3.array(z3.object({
    id: graphId,
    kind: z3.enum(["op", "output"]),
    label: z3.string(),
    detail: z3.string().optional(),
    artboardId: graphId.optional(),
    idx: z3.number().optional(),
    imageUrl: z3.string().nullable().optional()
  })),
  edges: z3.array(z3.tuple([graphId, graphId]))
});
var StudioWorkflowImageSchema = z3.object({
  id: graphId,
  url: z3.string(),
  label: z3.string(),
  jobId: graphId.optional(),
  inputVersions: z3.array(StudioFrameInputSchema).optional(),
  settings: StudioParametersSchema.optional(),
  model: z3.string().optional()
});
var StudioMediaFileSchema = z3.object({
  uuid: z3.string(),
  filename: z3.string(),
  contentType: z3.string(),
  size: z3.number(),
  publicUrl: z3.string().nullable(),
  nodeId: z3.string().nullable(),
  folderId: z3.string().nullable(),
  status: z3.enum(["ACTIVE", "PROCESSING", "FAILED", "DELETED"]),
  metadata: z3.record(z3.string(), z3.unknown()).optional(),
  createdAt: z3.string(),
  updatedAt: z3.string().optional()
});
var StudioMediaFolderSchema = z3.object({
  id: z3.string(),
  uuid: z3.string(),
  name: z3.string(),
  fileCount: z3.number(),
  createdAt: z3.string(),
  updatedAt: z3.string()
});
var StudioWalletTransactionSchema = z3.object({
  id: z3.string(),
  // Mirrors WalletTransactionType in packages/db/prisma/schema.prisma.
  type: z3.enum(["JOB_DEDUCTION", "TOP_UP", "REFUND", "SUBSCRIPTION_CREDIT", "TRAINING_HOLD", "TRAINING_CONFIRMED", "TRAINING_REFUND", "ADMIN_DEBIT", "REFERRAL_CREDIT"]),
  amount: z3.string(),
  balanceAfter: z3.string(),
  jobId: z3.string().nullable(),
  description: z3.string().nullable(),
  createdAt: z3.string()
});
var StudioNodePageSnapshotSchema = z3.object({
  revision: z3.number().int().nonnegative(),
  page: StudioWebPageSchema
}).strict();
var StudioNodePageSaveSchema = z3.object({
  expectedRevision: z3.number().int().nonnegative(),
  page: StudioWebPageSchema
}).strict();
var StudioNodeOutputWriteSchema = z3.object({
  output: StudioFrameOutputSchema,
  page: StudioWebPageSchema,
  graphId,
  dataUrl: z3.string().max(34 * 1024 * 1024).regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/)
}).strict();
var StudioQueueStateSchema = z3.object({
  paused: z3.boolean(),
  activeId: z3.string().nullable(),
  pendingIds: z3.array(z3.string()),
  heldIds: z3.array(z3.string()),
  revision: z3.number().int().nonnegative()
});
var StudioQueuePauseSchema = z3.object({ paused: z3.boolean() }).strict();
var StudioQueueHoldSchema = z3.object({ held: z3.boolean() }).strict();
var StudioQueueMoveSchema = z3.object({
  id: z3.string().min(1),
  beforeId: z3.string().min(1).nullable(),
  expectedRevision: z3.number().int().nonnegative()
}).strict();
var StudioActivityStatusSchema = z3.enum(["idle", "queued", "paused", "processing", "success", "error", "cancelled"]);
var StudioActivityActionSchema = z3.enum(["cancel", "retry", "pause", "resume", "move-up", "move-down"]);
var StudioActivityItemSchema = z3.object({
  id: z3.string(),
  title: z3.string(),
  description: z3.string().optional(),
  timestamp: z3.date().optional(),
  status: StudioActivityStatusSchema,
  nodeType: z3.string().optional(),
  errorMessage: z3.string().optional(),
  progress: z3.number().min(0).max(100).optional(),
  actions: z3.array(StudioActivityActionSchema).optional(),
  details: z3.array(z3.object({ label: z3.string(), value: z3.string() })).optional()
});
var LIBRARY_MAX_FILE_SIZE = 250 * 1024 * 1024;
var libraryFilename = z3.string().trim().min(1).max(255).refine(
  (value) => Array.from(value).every((character) => character !== "/" && character !== "\\" && character.charCodeAt(0) >= 32),
  "Use a filename without slashes or control characters."
);
var LibraryAssetSchema = StudioAssetSchema.pick({ id: true, name: true, mediaType: true, mimeType: true, size: true, sha256: true }).extend({
  id: z3.string().regex(/^[a-f0-9]{64}$/),
  name: libraryFilename,
  size: z3.number().int().nonnegative().max(LIBRARY_MAX_FILE_SIZE),
  mimeType: z3.string().regex(/^[a-z]+\/[a-z0-9.+-]+$/i).max(160)
}).strict().refine((asset) => asset.id === asset.sha256, "File identity must match its checksum.");
var LibraryRecordSchema = z3.object({
  asset: LibraryAssetSchema,
  revision: z3.number().int().positive(),
  createdAt: z3.string().datetime(),
  updatedAt: z3.string().datetime(),
  deletedAt: z3.string().datetime().nullable()
}).strict();
var LibraryPageSchema = z3.object({ files: z3.array(LibraryRecordSchema).max(100), nextCursor: z3.string().nullable() }).strict();
var LibraryChangeSchema = z3.object({
  expectedRevision: z3.number().int().positive(),
  name: libraryFilename.optional(),
  deleted: z3.boolean().optional()
}).strict();
var LibrarySyncStatusSchema = z3.object({
  state: z3.enum(["signed-out", "syncing", "synced", "offline", "error"]),
  pending: z3.number().int().nonnegative(),
  lastSyncedAt: z3.string().nullable(),
  error: z3.string().nullable()
}).strict();
var LibraryIndexStatusSchema = z3.object({ state: z3.enum(["pending", "ready", "error"]), error: z3.string().nullable() }).strict();
var LibraryDeviceFileSchema = z3.object({
  asset: LibraryAssetSchema,
  revision: z3.number().int().nonnegative(),
  createdAt: z3.string(),
  localUrl: z3.string().nullable(),
  state: z3.enum(["pending", "synced"])
}).strict();
var LibraryDeviceListingSchema = z3.object({ files: z3.array(LibraryDeviceFileSchema), status: LibrarySyncStatusSchema }).strict();
var COMPOSITION_BLENDS = ["normal", "multiply", "screen", "overlay", "darken", "lighten"];
var seconds = z3.number().finite().min(0).max(3600);
var COMPOSITION_MODEL_MODES = ["original", "pointCloud", "normal", "wireframe", "depth", "clay"];
var COMPOSITION_UP_DIRECTIONS = ["original", "-x", "+x", "-y", "+y", "-z", "+z"];
var vec3 = z3.tuple([coordinate, coordinate, coordinate]);
var CompositionCameraSchema = z3.object({
  position: vec3,
  target: vec3,
  zoom: z3.number().finite().positive().max(1e6),
  cameraType: z3.enum(["perspective", "orthographic"]),
  fov: z3.number().finite().min(1).max(179)
}).strict();
var CompositionLayer3DSchema = z3.object({
  camera: CompositionCameraSchema.optional(),
  /** Turns around the up axis across the clip; 0 holds the start camera. */
  spin: z3.number().finite().min(-20).max(20).optional(),
  lightIntensity: z3.number().finite().min(0).max(5).optional(),
  /** 'transparent' lets the layers below show through. */
  background: z3.union([z3.literal("transparent"), hexColor]).optional(),
  grid: z3.boolean().optional(),
  mode: z3.enum(COMPOSITION_MODEL_MODES).optional(),
  upDirection: z3.enum(COMPOSITION_UP_DIRECTIONS).optional(),
  /** Reserved for animation clips; no control writes these yet. */
  clip: z3.string().max(200).nullable().optional(),
  clipRate: z3.number().finite().min(0).max(4).optional()
}).strict();
var CompositionLayerSchema = z3.object({
  id: graphId,
  name: z3.string().min(1).max(200),
  type: z3.enum(["image", "video", "audio", "model", "text"]),
  /** The canvas frame the media came from, if any. */
  frameId: graphId.optional(),
  /** Media URL; a 3D layer points at the model file itself. */
  src: z3.string().min(1).max(4096).optional(),
  /** 3D layers: how the model is framed, lit and turned. Absent means the viewer's defaults. */
  three: CompositionLayer3DSchema.optional(),
  text: z3.string().max(2e3).optional(),
  fontFamily: z3.string().max(120).optional(),
  fontSize: z3.number().finite().min(4).max(2e3).optional(),
  fontWeight: z3.enum(["normal", "bold"]).optional(),
  color: hexColor.optional(),
  align: z3.enum(["left", "center", "right"]).optional(),
  x: coordinate,
  y: coordinate,
  width: z3.number().finite().positive().max(1e5),
  height: z3.number().finite().positive().max(1e5),
  rotation: z3.number().finite().min(-360).max(360),
  opacity: z3.number().finite().min(0).max(1),
  blend: z3.enum(COMPOSITION_BLENDS),
  /** When the layer appears on the timeline. */
  start: seconds,
  duration: z3.number().finite().positive().max(3600),
  /** How far into its source a video or audio layer begins playing. */
  offset: seconds,
  volume: z3.number().finite().min(0).max(1),
  muted: z3.boolean(),
  hidden: z3.boolean(),
  locked: z3.boolean(),
  /** Layers that share a group id are picked, moved and stacked as one; they sit together in the stack. */
  group: z3.string().min(1).max(64).optional()
}).strict().superRefine((layer, ctx) => {
  if (layer.type === "text" && layer.text === void 0) ctx.addIssue({ code: "custom", path: ["text"], message: "A text layer needs text." });
  if (layer.type !== "text" && !layer.src) ctx.addIssue({ code: "custom", path: ["src"], message: "A media layer needs a source." });
  if (layer.three && layer.type !== "model") ctx.addIssue({ code: "custom", path: ["three"], message: "Only a 3D layer has 3D settings." });
});
var COMPOSITION_MAX_SIZE = 65536;
var COMPOSITION_MAX_LAYERS = 4096;
var CompositionSchema = z3.object({
  width: z3.number().int().min(16).max(COMPOSITION_MAX_SIZE),
  height: z3.number().int().min(16).max(COMPOSITION_MAX_SIZE),
  fps: z3.number().int().min(1).max(120),
  duration: z3.number().finite().min(0.1).max(3600),
  background: hexColor,
  layers: z3.array(CompositionLayerSchema).max(COMPOSITION_MAX_LAYERS)
}).strict().superRefine((composition, ctx) => {
  const ids = /* @__PURE__ */ new Set();
  composition.layers.forEach((layer, index) => {
    if (ids.has(layer.id)) ctx.addIssue({ code: "custom", path: ["layers", index, "id"], message: "Layer IDs must be unique." });
    ids.add(layer.id);
  });
});

export {
  GENERATION_OPERATIONS,
  GRAPH_NODE_DEFS,
  APP_OPERATIONS,
  graphNodeDef,
  customWorkflowDef,
  CUSTOM_NODE_PREFIX,
  resolveNodeDef,
  graphNodeDefs,
  DesktopUpdateStateSchema,
  SceneAssetSchema,
  SceneEnvironmentSchema,
  SceneMaterialSchema,
  SceneLibrarySettingsSchema,
  ApplyLibraryAssetSchema,
  ShapeOptionsSchema,
  WEB_NODE_TYPES,
  WEB_CANVAS_NODES,
  WEB_NODES_WITHOUT_OPERATION,
  webNodeOperation,
  WEB_NODE_OPERATIONS,
  webOperationIssues,
  NO_EXECUTABLE_ADAPTER,
  LOCAL_CANVAS_FAMILIES,
  isStructuralNonOperation,
  executionInventory,
  decideGenericJobAdmission,
  StudioProviderConnectionSchema,
  StudioParametersSchema,
  GraphPortSchema,
  GraphExposedParameterSchema,
  GraphNodeDefSchema,
  GraphDocNodeSchema,
  GraphDocEdgeSchema,
  GraphDocSchema,
  graphExecutionIssues,
  StudioAssetSchema,
  StudioProvenanceSchema,
  StudioFrameVersionSchema,
  StudioFrameSchema,
  StudioGraphSchema,
  StudioProjectSchema,
  StudioWebNodeSchema,
  StudioWebEdgeSchema,
  StudioWebPageSchema,
  StudioDesktopVersionSchema,
  StudioDesktopFrameSchema,
  StudioDesktopSessionSchema,
  StudioFrameInputSchema,
  StudioFrameOutputSchema,
  StudioExecutionLocationSchema,
  StudioToolRequestSchema,
  StudioToolResultSchema,
  StudioSnapshotSchema,
  StudioSaveSchema,
  StudioAssetUploadSchema,
  FrameBoxSchema,
  FramePointSchema,
  FrameImageLevelSchema,
  FieldClassRuleSchema,
  FrameFieldSchema,
  FrameGroundSchema,
  FrameDeepZoomSchema,
  FrameLockSchema,
  FrameSidecarParamsSchema,
  ZONE_STANDABLE,
  ZONE_SKY,
  ZONE_TREES,
  zoneRule,
  zoneRules,
  zoneTakesFeet,
  frameOrder,
  CanvasImportVersionSchema,
  CanvasImportFrameSchema,
  CanvasImportSchema,
  ArtCanvasFrameSchema,
  ArtCanvasLineageSchema,
  StudioWorkflowImageSchema,
  StudioMediaFileSchema,
  StudioMediaFolderSchema,
  StudioWalletTransactionSchema,
  StudioNodePageSnapshotSchema,
  StudioNodePageSaveSchema,
  StudioNodeOutputWriteSchema,
  StudioQueueStateSchema,
  StudioQueuePauseSchema,
  StudioQueueHoldSchema,
  StudioQueueMoveSchema,
  StudioActivityStatusSchema,
  StudioActivityActionSchema,
  StudioActivityItemSchema,
  LIBRARY_MAX_FILE_SIZE,
  LibraryAssetSchema,
  LibraryRecordSchema,
  LibraryPageSchema,
  LibraryChangeSchema,
  LibrarySyncStatusSchema,
  LibraryIndexStatusSchema,
  LibraryDeviceFileSchema,
  LibraryDeviceListingSchema,
  COMPOSITION_BLENDS,
  COMPOSITION_MODEL_MODES,
  COMPOSITION_UP_DIRECTIONS,
  CompositionCameraSchema,
  CompositionLayer3DSchema,
  CompositionLayerSchema,
  COMPOSITION_MAX_SIZE,
  COMPOSITION_MAX_LAYERS,
  CompositionSchema
};
//# sourceMappingURL=chunk-GTI42F3R.js.map